// Blanks credentials in URLs before the browser SDKs (PostHog, Sentry) send
// them, by the rule the server applies to span URLs and error reports.
//
// A path segment is blanked when the router binds it to a token. A query value
// is blanked when its key, lowercased, contains one of FRAGMENTS or starts with
// `x-amz-`, or when it decodes to a query or to JSON that carries such a key.

export const FILTERED = "[FILTERED]";

const FRAGMENTS = [
  "key",
  "token",
  "jwt",
  "secret",
  "password",
  "passwd",
  "signature",
  "credential",
  "authorization",
  "cookie",
];

// posthog-js stores the first page URL and sends it on `/flags` as
// `person_properties.$initial_current_url`, which `before_send` never sees. It
// masks a query value only when `mask_personal_data_properties` is set and the
// key is an exact entry here: "key" does not cover "api_key".
export const POSTHOG_PERSONAL_DATA_PROPERTIES = [
  ...FRAGMENTS,
  "api_key",
  "access_token",
  "Access_Token",
  "_csrf_token",
  "client_secret",
  "X-Amz-Credential",
  "X-Amz-Signature",
  "x-amz-credential",
  "x-amz-signature",
];

// Query keys above are blanked on every route. A path segment has no key, so
// this is every route in `SauronWeb.Router` whose path carries a secret, up to
// that segment: `/invite/:token` covers `/invite/:token/start`. A `:param`
// before it matches any one segment.
// `test/sauron_web/router_token_routes_test.exs` fails when the router gains
// one this list lacks, and when a `:param` before the secret would be compared
// literally.
const TOKEN_ROUTES = [
  "/invite/signin/:signin_token",
  "/invite/:token",
  "/auth/magic-link/:token",
  "/webhook/dockerhub/:token",
];

// A value ends at a URL delimiter, or at a quote or backslash when the URL sits
// inside JSON or an HTML attribute, as in an autocapture element chain.
const SEGMENT = `[^/?#&\\s"'<>\\\\]+`;
const BEFORE_TOKEN = tokenRoutePattern(TOKEN_ROUTES);
const TOKEN_PATH = new RegExp(`^${BEFORE_TOKEN}[^/]`);
const TOKEN_SEGMENT = new RegExp(`(${BEFORE_TOKEN})${SEGMENT}`, "g");
const HAS_TOKEN_SEGMENT = new RegExp(BEFORE_TOKEN);
const QUERY_PAIR = /([?&])([^?&=#\s"'<>\\]+)=([^&#\s"'<>\\]*)/g;

// Deeper than any event the SDKs build. A recording's DOM snapshot nests two
// levels per element.
const MAX_DEPTH = 500;

const ERROR_KEYS = ["name", "message", "stack"];

// Where the SDKs put URLs: a Sentry event, transaction or replay event; a
// replay breadcrumb or span; a PostHog event.
const URL_FIELDS = [
  ["request"],
  ["urls"],
  ["breadcrumbs"],
  ["spans"],
  ["data", "payload"],
  ["properties", "$current_url"],
  ["properties", "$referrer"],
  ["properties", "$session_entry_url"],
  ["properties", "$elements_chain"],
  ["properties", "$heatmap_data"],
  ["$set_once"],
];

// The regex source for a path up to the token segment of any of `routes`.
export function tokenRoutePattern(routes) {
  const prefixes = routes.map((route) =>
    route
      .split("/")
      .filter(Boolean)
      .slice(0, -1)
      .map((segment) => (segment.startsWith(":") ? SEGMENT : escapeRegExp(segment)))
      .join("/"),
  );

  return `/(?:${prefixes.join("|")})/`;
}

// Whether `pathname` is one of the pages whose own path is a credential.
export function isTokenPath(pathname) {
  return typeof pathname === "string" && TOKEN_PATH.test(pathname);
}

export function sensitiveKey(key) {
  const lower = decode(key).toLowerCase();
  return lower.startsWith("x-amz-") || FRAGMENTS.some((fragment) => lower.includes(fragment));
}

// Blanks every token path segment and sensitive query value in `value`, which
// may be a URL, a bare path, or any text with URLs in it.
export function redactUrl(value) {
  if (typeof value !== "string" || !mayCarryCredential(value)) return value;

  return value
    .replace(TOKEN_SEGMENT, (_match, prefix) => prefix + FILTERED)
    .replace(QUERY_PAIR, (match, separator, key, pairValue) =>
      sensitiveKey(key) || nestedCredential(pairValue) ? `${separator}${key}=${FILTERED}` : match,
    );
}

// `redactUrl` applied to every string in a JSON-shaped value, keys included:
// an SDK's event, breadcrumb or recording event. Two keys that redact alike
// keep the later one's value. An `Error` becomes `{name, message, stack}`,
// since Sentry serializes one only after every hook has run. Other objects (a
// Date, a Map) are kept as they are. An object met again, a cycle included,
// maps to the same copy. An entry that cannot be read, or sits deeper than
// MAX_DEPTH, becomes FILTERED.
export function redactUrls(value) {
  return redact(value, new WeakMap(), 0);
}

// What the SDK hooks run: a hook that throws loses the event, and Sentry sends
// its own error in its place, unredacted. Should walking the event fail, the
// event goes out with its URL fields emptied.
export function redactEvent(event) {
  try {
    return redactUrls(event);
  } catch {
    try {
      return blankUrlFields(event);
    } catch {
      return event;
    }
  }
}

function redact(value, copies, depth) {
  if (typeof value === "string") return redactUrl(value);
  if (value === null || typeof value !== "object") return value;
  if (copies.has(value)) return copies.get(value);
  if (depth >= MAX_DEPTH) return FILTERED;

  if (Array.isArray(value)) {
    const out = [];
    copies.set(value, out);
    for (let index = 0; index < value.length; index++) {
      out[index] = redactEntry(value, index, copies, depth + 1);
    }
    return out;
  }

  const keys = isError(value) ? ERROR_KEYS : isPlainObject(value) ? Object.keys(value) : null;
  if (!keys) return value;

  const out = {};
  copies.set(value, out);
  for (const key of keys) out[redactUrl(key)] = redactEntry(value, key, copies, depth + 1);
  return out;
}

// A throwing getter or a revoked proxy blanks its own entry, not the value.
function redactEntry(object, key, copies, depth) {
  try {
    return redact(object[key], copies, depth);
  } catch {
    return FILTERED;
  }
}

function blankUrlFields(event) {
  for (const path of URL_FIELDS) {
    try {
      const key = path[path.length - 1];
      const parent = path.slice(0, -1).reduce((object, step) => object?.[step], event);
      const value = parent?.[key];
      if (typeof value === "string") parent[key] = FILTERED;
      else if (Array.isArray(value)) parent[key] = [];
      else if (typeof value === "object" && value !== null) parent[key] = {};
    } catch {
      // A field it cannot reach is left as it is.
    }
  }

  return event;
}

function mayCarryCredential(value) {
  return value.includes("=") || HAS_TOKEN_SEGMENT.test(value);
}

function nestedCredential(value) {
  if (/=|%3d/i.test(value) && queryCredential(decode(value))) return true;

  return /^(?:[[{]|%5b|%7b)/i.test(value) && jsonCredential(decode(value));
}

function queryCredential(query) {
  return query.split(/[?&]/).some((pair) => {
    const equals = pair.indexOf("=");
    return equals > 0 && sensitiveKey(pair.slice(0, equals));
  });
}

function jsonCredential(text) {
  try {
    return hasSensitiveKey(JSON.parse(text), 0);
  } catch {
    return false;
  }
}

// Whether any key of `value`, at any depth, is sensitive. Past MAX_DEPTH the
// answer is yes, so a value too deep to read is blanked.
function hasSensitiveKey(value, depth) {
  if (value === null || typeof value !== "object") return false;
  if (depth >= MAX_DEPTH) return true;

  return Object.entries(value).some(
    ([key, entry]) =>
      (!Array.isArray(value) && sensitiveKey(key)) || hasSensitiveKey(entry, depth + 1),
  );
}

function decode(value) {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
}

// An error from another realm, such as an iframe, fails `instanceof Error`.
function isError(value) {
  return value instanceof Error || Object.prototype.toString.call(value) === "[object Error]";
}

function isPlainObject(value) {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
